import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Sparkles, X, Mic, Compass } from "lucide-react";

const GROUNDED_INTENTS = [
  {
    keywords: ["vistara", "web3"],
    reply: "Vistara is a Web3 infrastructure landing architecture built with Next.js, Tailwind CSS, and Framer Motion. Source is at github.com/Maverick-list/vistara, and live at vistara.network. Read the case study at /works/vistara.",
  },
  {
    keywords: ["mavecode", "course", "learning", "platform"],
    reply: "MavecodeCourse is a full-stack learning platform featuring course progression tracking, Next.js, Node.js, and MongoDB. Source is at github.com/Maverick-list/mavecode-course, live at mavecode.my.id. See /works/mavecode-course.",
  },
  {
    keywords: ["mavementor", "mentor", "rag", "retrieval", "llm"],
    reply: "MaveMentor AI is an applied AI conversational learning pipeline using FastAPI, Python, and vector similarity search. It is an engineering prototype at github.com/Maverick-list/mavementor-ai. Read the case study at /works/mavementor-ai.",
  },
  {
    keywords: ["slr", "curation", "research", "academic"],
    reply: "AI Content Curation is an academic Systematic Literature Review (SLR) research tool designed to automate literature search indexing, duplicate hashing, and metadata extraction. See /works/ai-content-curation.",
  },
  {
    keywords: ["article", "essay", "writing", "notes", "design system"],
    reply: "Firza has published 3 field notes: 'Design systems are decisions, not components', 'Informatics and the craft of software', and 'RAG pipelines in production'. Browse them all at /articles.",
  },
  {
    keywords: ["stack", "tech", "skill", "language", "framework"],
    reply: "Primary engineering stack: TypeScript, React, Next.js (App Router), Tailwind CSS, Python, FastAPI, MongoDB, PostgreSQL, and applied AI pipelines (embeddings, RAG, prompt evaluation).",
  },
  {
    keywords: ["contact", "email", "hire", "reach"],
    reply: "You can reach Firza directly via email at firzailmidja@gmail.com, or through the contact form at /contact.",
  },
];

export default function VoiceChatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: "Hello! I am Firza's portfolio navigator. Ask about verified projects (Vistara, MavecodeCourse, MaveMentor AI, AI Content Curation), field notes, or stack capabilities.",
    },
  ]);
  const [text, setText] = useState("");
  const [listening, setListening] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = () => {
    const query = text.trim();
    if (!query) return;

    const userMsg = { role: "user", text: query };
    setMessages((m) => [...m, userMsg]);
    setText("");

    // Grounded response matching
    const qLower = query.toLowerCase();
    const matched = GROUNDED_INTENTS.find((intent) =>
      intent.keywords.some((kw) => qLower.includes(kw))
    );

    const reply = matched
      ? matched.reply
      : "I'm a portfolio navigation guide grounded in Firza's verified repository data. You can ask about his projects (Vistara, MavecodeCourse, MaveMentor AI, AI Content Curation), articles on design systems or RAG, or his contact information.";

    setTimeout(() => {
      setMessages((m) => [...m, { role: "bot", text: reply }]);
    }, 350);
  };

  const toggleMic = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }
    if (listening) {
      setListening(false);
      return;
    }
    try {
      const recognition = new SpeechRecognition();
      recognition.lang = "en-US";
      recognition.interimResults = false;
      recognition.onstart = () => setListening(true);
      recognition.onresult = (e) => {
        const transcript = e.results[0][0].transcript;
        setText(transcript);
        setListening(false);
      };
      recognition.onerror = () => setListening(false);
      recognition.onend = () => setListening(false);
      recognition.start();
    } catch {
      setListening(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Open portfolio guide"
        title="Open portfolio guide"
        data-testid="chatbot-fab"
        className="fixed bottom-6 right-6 z-40 inline-flex h-13 w-13 p-3.5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_12px_35px_hsl(var(--accent)/0.45)] hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-primary transition-transform hover:scale-105"
      >
        <Sparkles className="h-5 w-5" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed bottom-24 right-6 z-40 w-[380px] max-w-[92vw] rounded-2xl border border-border/80 bg-[#0d0d12]/95 backdrop-blur-md shadow-2xl overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/60 bg-card/40">
              <div className="flex items-center gap-2">
                <Compass className="h-4 w-4 text-primary" />
                <div>
                  <span className="font-mono text-[11px] tracking-[0.18em] uppercase font-medium text-foreground">
                    Portfolio Guide
                  </span>
                  <span className="block font-mono text-[9px] text-muted-foreground">
                    Grounded in source facts
                  </span>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close portfolio guide"
                className="text-muted-foreground hover:text-foreground p-1 rounded-md"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="h-72 overflow-y-auto px-4 py-3 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] text-[13px] px-3.5 py-2.5 rounded-xl leading-relaxed ${
                      m.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary/70 border border-border/60 text-foreground/90"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
            </div>

            {/* Input Bar */}
            <div className="flex items-center gap-2 px-3 py-3 border-t border-border/60 bg-card/30">
              <button
                onClick={toggleMic}
                className={`h-9 w-9 inline-flex items-center justify-center rounded-full border border-border/60 transition-colors ${
                  listening
                    ? "bg-rose-500/20 text-rose-300 border-rose-400/50 animate-pulse"
                    : "hover:bg-secondary/60 text-muted-foreground hover:text-foreground"
                }`}
                aria-label="Voice input"
                title="Voice input"
              >
                <Mic className="h-4 w-4" />
              </button>
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about projects, articles, stack..."
                aria-label="Message prompt"
                className="flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/70 text-foreground"
              />
              <button
                onClick={send}
                aria-label="Send message"
                className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
